import React, { useEffect, useState } from "react";
import { MdClose, MdCheckCircle, MdError, MdPerson, MdBusiness } from "react-icons/md";
import api from "../../../../services/axios";
import Modal from "../../../../components/modal";
import { WeeklyReportLogItem } from "../../../../interfaces/Financial";

interface Props {
  onClose: () => void;
}

const fmtDateTime = (iso: string) =>
  new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });

const fmtPeriod = (a: string, b: string) =>
  `${new Date(a).toLocaleDateString("pt-BR")} – ${new Date(b).toLocaleDateString("pt-BR")}`;

const fmtHours = (h: number) => `${h.toLocaleString("pt-BR", { maximumFractionDigits: 1 })} h`;

const ReportLogModal: React.FC<Props> = ({ onClose }) => {
  const [items, setItems] = useState<WeeklyReportLogItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await api.get<WeeklyReportLogItem[]>("/Financial/WeeklyReportLog", {
          params: { take: 200 },
        });
        if (active) setItems(res.data);
      } catch {
        if (active) setItems([]);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  return (
    <Modal onClose={onClose}>
      <div
        className="flex h-[80vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-dv-lg dark:bg-navy-800"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-3 dark:border-gray-700">
          <div>
            <h3 className="font-bold text-navy-700 dark:text-white">Histórico de envios</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">Reports de horas enviados (clientes e colaboradores)</p>
          </div>
          <button
            aria-label="Fechar"
            onClick={onClose}
            className="rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-navy-700"
          >
            <MdClose className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        <div className="flex-1 overflow-auto px-5 py-3">
          {loading ? (
            <p className="py-10 text-center text-sm text-gray-500 dark:text-gray-400">Carregando...</p>
          ) : items.length === 0 ? (
            <p className="py-10 text-center text-sm text-gray-500 dark:text-gray-400">Nenhum envio registrado ainda.</p>
          ) : (
            <div className="flex flex-col divide-y divide-gray-100 dark:divide-gray-700">
              {items.map((it) => (
                <div key={it.id} className="flex items-center gap-3 py-2.5">
                  <span
                    className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
                      it.recipientType === "Client"
                        ? "bg-dv-blue-50 text-dv-blue-600 dark:bg-dv-blue-900/40 dark:text-dv-blue-300"
                        : "bg-dv-cyan-50 text-dv-cyan-600 dark:bg-dv-cyan-900/40 dark:text-dv-cyan-300"
                    }`}
                    title={it.recipientType}
                  >
                    {it.recipientType === "Client" ? <MdBusiness className="h-4 w-4" aria-hidden="true" /> : <MdPerson className="h-4 w-4" aria-hidden="true" />}
                  </span>

                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-navy-700 dark:text-white">
                      {it.recipientName}
                    </p>
                    <p className="truncate text-xs text-gray-500 dark:text-gray-400">
                      {it.email ?? "sem e-mail"} · {fmtPeriod(it.periodStart, it.periodEnd)}
                    </p>
                  </div>

                  <div className="hidden text-right sm:block">
                    <p className="text-xs font-semibold tabular-nums text-navy-700 dark:text-white">{fmtHours(it.totalHours)}</p>
                    <p className="text-[11px] tabular-nums text-gray-500 dark:text-gray-400">{fmtDateTime(it.sentAt)}</p>
                  </div>

                  {it.success ? (
                    <MdCheckCircle className="h-5 w-5 shrink-0 text-emerald-500" title="Enviado" aria-label="Enviado" />
                  ) : (
                    <MdError className="h-5 w-5 shrink-0 text-red-500" title={it.errorMessage ?? "Falha"} aria-label="Falha no envio" />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default ReportLogModal;
